import { getLocalStorage, setLocalStorage, removeLocalStorage } from './localStrage';

// マイグレーション実行履歴を保存するキー
const MIGRATION_HISTORY_KEY = 'migration_history';

// マイグレーションの型定義
type Migration = {
    version: string;
    execute: (userId: string | number) => Promise<void>;
};

// マイグレーションのリスト（バージョン順に定義）
// バージョンは数値で管理（1から開始）
const MIGRATIONS: Migration[] = [
    {
        version: '1',
        execute: async (userId: string | number) => {
            // レイアウト情報をクリア
            await removeLocalStorage(userId, 'panel_layout_info');
        },
    },
    // 将来的に追加するマイグレーションはここに追加
    // {
    //     version: '2',
    //     execute: async (userId: string | number) => {
    //         // 他のマイグレーション処理
    //     },
    // },
];

/**
 * 実行済みマイグレーションのバージョンを取得（最後に実行したバージョン）
 * 後方互換性のため、配列形式のデータも読み取れるようにする
 */
async function getExecutedVersion(userId: string | number): Promise<number> {
    const historyStr = await getLocalStorage(userId, MIGRATION_HISTORY_KEY);
    if (!historyStr) return 0;
    try {
        const parsed = JSON.parse(historyStr);
        // 配列形式の場合は最大値を取得（後方互換性）
        if (Array.isArray(parsed)) {
            const numericVersions = parsed
                .map((v) => parseInt(String(v), 10))
                .filter((v) => !isNaN(v));
            return numericVersions.length > 0 ? Math.max(...numericVersions) : 0;
        }
        // 数値形式の場合
        const version = parseInt(String(parsed), 10);
        return isNaN(version) ? 0 : version;
    } catch {
        return 0;
    }
}

/**
 * マイグレーション実行履歴を保存（最後に実行したバージョンのみ）
 */
async function saveMigrationHistory(
    userId: string | number,
    version: number,
): Promise<void> {
    await setLocalStorage(userId, MIGRATION_HISTORY_KEY, JSON.stringify(version));
}

/**
 * 未実行のマイグレーションを実行
 */
export async function runMigrations(userId: string | number): Promise<void> {
    const executedVersion = await getExecutedVersion(userId);
    let maxExecutedVersion = executedVersion;

    for (const migration of MIGRATIONS) {
        const migrationVersion = parseInt(migration.version, 10);
        // バージョンが数値として有効でない場合はスキップ
        if (isNaN(migrationVersion)) {
            continue;
        }

        // ローカルストレージのバージョンより大きい場合のみ実行
        if (migrationVersion <= executedVersion) {
            continue;
        }

        try {
            await migration.execute(userId);
            // 実行したバージョンが最大値より大きければ更新
            if (migrationVersion > maxExecutedVersion) {
                maxExecutedVersion = migrationVersion;
            }
        } catch (error) {
            // エラーが発生しても次のマイグレーションを実行
        }
    }

    // 実行履歴を更新（最後に実行したバージョンのみ）
    if (maxExecutedVersion > executedVersion) {
        await saveMigrationHistory(userId, maxExecutedVersion);
    }
}

